import { TextField, Typography } from '@gupshup-frontend/gupshup-ts-components';
import {
  isAuthentication, isCarousel, isLto, isMarketing, isPasskey, MARKETING_OPT_OUT_FOOTER,
  hasRequestContactInfo,
} from '../state';
import { checkFooterTextFormat } from '../validate';
import { friendlyError } from './errorMessages';

const FOOTER_MAX_CHARS = 60;

// 10. Footer — plain TextField, no variables allowed.
const FooterSection = ({ state, dispatch, errors, disabled }) => {
  if (isLto(state) || isCarousel(state) || (isAuthentication(state) && !isPasskey(state)) || hasRequestContactInfo(state)) {
    // Matches gs-ui-templates Footer/index.tsx's own hide rule: LTO and Carousel templates carry
    // no footer in WhatsApp's spec, non-Passkey auth templates use a fixed server body with no
    // footer either, and Request Contact Info rejects any footer outright (ValidationUtils.
    // validateRequestContactInfoTemplate).
    return null;
  }

  // Marketing opt-out overwrites the footer unconditionally server-side (state.js's
  // MARKETING_OPT_OUT_FOOTER comment; payload.js sends this exact string in place of
  // footer.footerText.value while active — see its own optOutActive). Mirrored here for DISPLAY
  // only: the field goes read-only and shows the fixed opt-out text, but state.footer.footerText
  // itself is left completely untouched, so unticking the checkbox in sections/Buttons.js brings
  // the user's own text straight back with nothing to retype.
  const optOutActive = isMarketing(state) && state.optOutButton;

  const { value, error: liveError } = state.footer.footerText;
  const displayValue = optOutActive ? MARKETING_OPT_OUT_FOOTER : value;
  const submitError = errors['footer.footerText.value'];
  // validate.js skips footer validation outright while opt-out is active (the same optOutActive
  // gate) — suppress any leftover per-keystroke error too, or a stale format error typed before the
  // checkbox was ticked would render under a field that's no longer showing the text it was about.
  const displayError = optOutActive ? '' : (liveError || submitError);

  const handleChange = (event) => {
    const { value: nextValue } = event.target;
    dispatch({
      type: 'merge',
      path: 'footer.footerText',
      value: { value: nextValue, error: checkFooterTextFormat(nextValue) },
    });
  };

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-footer">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-footer-heading">Footer</Typography>
          <span className="br-tpl-form-optional-pill" data-testid="bloomreach-templates-form-footer-optional">
            Optional
          </span>
        </div>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Add a 60 character footer to your message. Variables are not supported in the footer.
      </Typography>
      {optOutActive && (
        <Typography
          variant="caption"
          className="br-tpl-form-helper-text"
          data-testid="bloomreach-templates-form-footer-optout-caption"
        >
          The marketing opt-out button sets this footer automatically while it is on.
        </Typography>
      )}
      <TextField
        fullWidth
        label="Footer text"
        value={displayValue}
        onChange={handleChange}
        disabled={disabled || optOutActive}
        error={Boolean(displayError)}
        helperText={friendlyError(displayError)}
        autoComplete="off"
        inputProps={{ maxLength: FOOTER_MAX_CHARS, 'data-testid': 'bloomreach-templates-form-footer-textfield' }}
      />
      <Typography variant="caption" className="br-tpl-form-char-count" data-testid="bloomreach-templates-form-footer-count">
        {`${displayValue.length} / ${FOOTER_MAX_CHARS}`}
      </Typography>
    </div>
  );
};

export default FooterSection;
